<template>
  <div v-if="!isNoGames" ref="sliderRef" :class="$style.gamesCatalogueSliderContainer">
    <CatalogueSliderHeader
      :item-id="collection.id"
      :header-text="headerText"
      :icon="collection.icon && collection.icon.url"
      :name="collection.name"
      :show-all-title="showAllTitle"
      :is-loading="!flickingRef"
      :show-all-link="useStoreRoot().localePath(`/${collection.id}`)"
      :no-navigation="Boolean(gamesList && slidesPerView >= gamesList.length)"
      @showAll="openCollection(collection.id)"
    />
    <template v-if="!isLoading">
      <Flicking
        ref="flickingRef"
        :key="reloadKey"
        hide-before-init
        :options="{
          bound: true,
          align: 'prev',
          panelsPerView: slidesPerView,
          inputType: isDesktop ? [] : ['touch', 'mouse'],
        }"
        :class="$style.gamesCatalogueSlider"
        @ready="sliderOnReady"
        @after-resize="sliderOnAfterResize"
      >
        <div v-for="(game, i) in gamesList" :key="i" :class="$style.gamesCatalogueRowSliderItem">
          <GameThumbnail
            :game="game"
            :gtm-action="gtmAction"
            :favorite-games="favoriteGames"
            :collection-id="collection.id"
            @add-to-favorite="addToFavorite"
            @remove-from-favorite="removeFromFavorite"
          />
        </div>
      </Flicking>
    </template>
    <CatalogueRowSkeleton v-else :slides-per-view="slidesPerView" :is-game-page="isGamePage" />
  </div>
</template>

<script lang="ts" setup>
import { Arrow } from '@egjs/flicking-plugins';
import Flicking, { type FlickingEvents } from '@egjs/vue3-flicking';
import { GameThumbnail } from '~/components/lib/mainPage';
import CatalogueRowSkeleton from '~/components/mainPage/catalogueRowSkeleton.vue';
import CatalogueSliderHeader from '~/components/mainPage/catalogueSliderHeader.vue';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsGamesFavourite } from '~/stores/softswiss/gamesFavourite';
import { useStoreCategoryPage } from '~/stores/strapi/categoryPage';
import {
  catalogueThumbsCount,
  catalogueThumbsCountGamePage,
} from '~/utils/defaultData/catalogueThumbsCount';
import type { FlickingType } from '~/types/banners';
import type { ISsGameMainPage, ISsGame } from '~/types/softswiss/ssGames';
import '@egjs/vue3-flicking/dist/flicking.css';

const props = defineProps({
  collection: {
    type: Object as PropType<ISsGameMainPage>,
    required: true,
  },
  gamesList: {
    type: Array as PropType<ISsGame[] | null>,
    default: null,
  },
  showAllTitle: {
    type: String,
    default: '',
  },
  gtmAction: {
    type: String,
    required: true,
  },
  headerText: {
    type: String,
    default: '',
  },
  isGamePage: {
    type: Boolean,
    default: false,
  },
});

const storeCategoryPage = useStoreCategoryPage();
const storeSsGamesFavourite = useStoreSsGamesFavourite();
const sliderRef = ref<null | HTMLElement>(null);
const reloadKey = ref(Date.now());
const flickingRef = ref<null | FlickingType>(null);

const slidesPerView = computed(() => {
  return props.isGamePage
    ? catalogueThumbsCountGamePage(useStoreRoot().screen.width)
    : catalogueThumbsCount(useStoreRoot().screen.width);
});
const isLoading = computed(() => {
  return !props.gamesList || (props.gamesList && !props.gamesList.length);
});
const isNoGames = computed(() => {
  return props.gamesList && !props.gamesList.length;
});
const isDesktop = computed(() => usePlatform().isDesktop);
const favoriteGames = computed(() => storeSsGamesFavourite.gameIds);

const addToFavorite = storeSsGamesFavourite.addToFavorite;
const removeFromFavorite = storeSsGamesFavourite.removeFromFavorite;

const openCollection = async (categoryId: string) => {
  storeCategoryPage.setSpecial(categoryId);

  await navigateTo({
    path: useStoreRoot().localePath(`/${categoryId}`),
  });

  if (import.meta.browser && window) {
    window.scrollTo({
      top: 0,
    });
  }
};

const sliderOnReady = (event: FlickingEvents['ready']) => {
  const currentTarget = event.currentTarget;
  currentTarget?.addPlugins(
    new Arrow({
      parentEl: sliderRef.value,
    })
  );
};

const sliderOnAfterResize = (event: FlickingEvents['afterResize']) => {
  const currentTarget = event.currentTarget;
  currentTarget.activePlugins.forEach((el) => el.update(currentTarget));
};

watch(
  () => props.gamesList,
  () => (reloadKey.value = Date.now()),
  { deep: true }
);
</script>

<style lang="scss" module src="./catalogueRow.scss"></style>
